import uid from "common/utils/uid";
import type { DocEntityType } from "../doc/docEntityTypes";

// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __docEntityIdSymbol: unique symbol;

/**
 * A nominal type representing a typed string identifier for a Doc entity extending `DocEntityBase`.
 *
 * When to use:
 * - The entity extends `DocEntityBase` and has its own respective table on the Doc
 * - The entity's CRUD operations are fully type-safe around the handling of the entity's nominally typed id
 *
 * When not to use:
 * - Do not use for an entity's `id` field; use {@link PrimaryEntityId}.
 *
 * Carries the same FK semantics as {@link ForeignEntityId} for schema generation and
 * {@link DocEntityForeignKeyConstraints} validation.
 *
 * Learn more about each entity id type in the [EntityId documentation](./EntityId.md)
 */
export type DocEntityId<T extends DocEntityType = DocEntityType> = string & {
  readonly [__docEntityIdSymbol]: T;
};

/**
 * Checks if a type T is (or contains) a DocEntityId.
 */
export type IsDocEntityId<T> = true extends (T extends { readonly [__docEntityIdSymbol]: any } ? true : false)
  ? true
  : false;

/**
 * Extracts the target entity type from a DocEntityId field.
 */
export type ExtractDocEntityTarget<T> = T extends { readonly [__docEntityIdSymbol]: infer Target } ? Target : never;

/**
 * Creates a new DocEntityId using `uid`. This is a nominal type, so the value is just a string.
 */
export function docEntityIdNew<T extends DocEntityType>(_entityType: T): DocEntityId<T> {
  return uid() as DocEntityId<T>;
}

/**
 * Creates a new DocEntityId using a string argument. This is a nominal type, so the value is just a string.
 */
export function docEntityIdFromString<T extends DocEntityType>(_entityType: T, s: string): DocEntityId<T> {
  return s as DocEntityId<T>;
}
